/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, MailCheck, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { saveTier1, sendEmailOTP, verifyEmailOTP, isValidCF, isValidPhone } from '@/lib/onboarding';
import { validateCodiceFiscale } from '@/lib/antiFraud';
import { ErrorAlert } from './parts';

// -----------------------------------------------------------------------------
// TIER 1: Personal identity + Email OTP verification
// -----------------------------------------------------------------------------
export const Tier1Step = ({ user, onDone }) => {
  const [form, setForm] = useState({
    first_name: user?.first_name || (user?.full_name || '').split(' ')[0] || '',
    last_name: user?.last_name || (user?.full_name || '').split(' ').slice(1).join(' ') || '',
    codice_fiscale: user?.codice_fiscale || '',
    mobile_phone: user?.mobile_phone || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // OTP sub-state
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpBusy, setOtpBusy] = useState(false);
  const [emailVerified, setEmailVerified] = useState(!!user?.email_verified);

  const canSubmitProfile =
    form.first_name.trim().length > 0 &&
    form.last_name.trim().length > 0 &&
    isValidCF(form.codice_fiscale) &&
    isValidPhone(form.mobile_phone);

  const handleSave = async () => {
    setSaving(true); setError(null);
    try {
      await saveTier1(form);
      toast.success('Profile updated');
    } catch (e) { setError(e); }
    finally { setSaving(false); }
  };

  const handleSendOtp = async () => {
    setOtpBusy(true); setError(null);
    try {
      await sendEmailOTP();
      setOtpSent(true);
      toast.success('Verification code sent to your email');
    } catch (e) { setError(e); }
    finally { setOtpBusy(false); }
  };

  const handleVerifyOtp = async () => {
    setOtpBusy(true); setError(null);
    try {
      await verifyEmailOTP(otpCode);
      setEmailVerified(true);
      toast.success('Email verified successfully');
      onDone && onDone();
    } catch (e) { setError(e); }
    finally { setOtpBusy(false); }
  };

  return (
    <div data-testid="tier1-step">
      <div className="mb-4">
        <h2 className="font-display font-bold text-xl mb-1">Tier 1 — Personal identity</h2>
        <p className="text-sm text-muted-foreground">
          Provide your legal identity details. This information is required by Italian regulations for
          healthcare professionals and is never shown publicly.
        </p>
      </div>
      <ErrorAlert error={error} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="first_name">First name</Label>
          <Input id="first_name" data-testid="tier1-first-name" value={form.first_name}
            onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
        </div>
        <div>
          <Label htmlFor="last_name">Last name</Label>
          <Input id="last_name" data-testid="tier1-last-name" value={form.last_name}
            onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
        </div>
        <div>
          <Label htmlFor="cf">Codice Fiscale</Label>
          <Input id="cf" data-testid="tier1-cf" value={form.codice_fiscale} maxLength={16}
            style={{ textTransform: 'uppercase' }}
            placeholder="RSSMRA85M01H501Z"
            onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value.toUpperCase() })} />
          {form.codice_fiscale && !isValidCF(form.codice_fiscale) && (
            <p className="text-xs text-destructive mt-1">Invalid Codice Fiscale format.</p>
          )}
          {form.codice_fiscale && isValidCF(form.codice_fiscale) && !validateCodiceFiscale(form.codice_fiscale).valid && (
            <p className="text-xs text-warning mt-1" data-testid="tier1-cf-checksum-warning">Attenzione: {validateCodiceFiscale(form.codice_fiscale).reason}. Verifica di averlo digitato correttamente.</p>
          )}
          {form.codice_fiscale && validateCodiceFiscale(form.codice_fiscale).valid && (
            <p className="text-xs text-success mt-1" data-testid="tier1-cf-checksum-ok">Codice fiscale formalmente valido (checksum OK).</p>
          )}
        </div>
        <div>
          <Label htmlFor="phone">Mobile phone</Label>
          <Input id="phone" data-testid="tier1-phone" placeholder="+39 333 1234567" value={form.mobile_phone}
            onChange={(e) => setForm({ ...form, mobile_phone: e.target.value })} />
          {form.mobile_phone && !isValidPhone(form.mobile_phone) && (
            <p className="text-xs text-destructive mt-1">Use international format, e.g. +39 333 1234567.</p>
          )}
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        <Button onClick={handleSave} disabled={!canSubmitProfile || saving} data-testid="tier1-save-btn">
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Save
        </Button>
      </div>

      <div className="mt-6 pt-6 border-t border-border">
        <h3 className="font-display font-semibold text-base mb-1 flex items-center gap-2">
          <MailCheck className="h-4 w-4 text-primary" /> Email verification
        </h3>
        {emailVerified ? (
          <div className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-300 mt-2"
            data-testid="tier1-email-verified">
            <CheckCircle2 className="h-4 w-4" /> Your email <strong>{user?.email}</strong> is verified.
          </div>
        ) : (
          <div className="mt-2">
            <p className="text-sm text-muted-foreground mb-3">
              A 6-digit verification code will be sent to <strong>{user?.email}</strong>.
            </p>
            {!otpSent ? (
              <Button variant="secondary" onClick={handleSendOtp} disabled={otpBusy} data-testid="tier1-send-otp">
                {otpBusy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Send verification code
              </Button>
            ) : (
              <div className="flex gap-2 items-end flex-wrap">
                <div className="flex-1 min-w-[160px]">
                  <Label htmlFor="otp">6-digit code</Label>
                  <Input id="otp" data-testid="tier1-otp-code" maxLength={6}
                    inputMode="numeric" pattern="\d{6}" placeholder="123456"
                    value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))} />
                </div>
                <Button onClick={handleVerifyOtp} disabled={otpBusy || otpCode.length !== 6}
                  data-testid="tier1-verify-otp">
                  {otpBusy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Verify
                </Button>
                <Button variant="ghost" size="sm" onClick={handleSendOtp} disabled={otpBusy}
                  data-testid="tier1-resend-otp">Resend</Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
